import {
  IonContent,
  IonIcon,
  IonNav,
  IonPage,
  IonRouterLink,
  IonText,
  IonImg,
  IonLabel,
  useIonAlert,
  useIonModal,
  useIonToast,
} from '@ionic/react'
import { DetailContent, FooterButtons, StyleIonHeader, TextStyle } from '../../../components'
import { observer } from 'mobx-react'
import { RouteComponentProps } from 'react-router'
import { useContext, useEffect } from 'react'
import { useIonViewWillEnter } from '@ionic/react'
import styled from 'styled-components'
import { StoresContext } from '../../../provider/StoreProvider'
import {
  gridOutline,
  chevronForward,
  warning,
  checkbox,
  shareSocialOutline,
  bookmarkOutline,
  bookmark,
} from 'ionicons/icons'
import moment from 'moment-timezone'
import { SaleOrderModal } from './modals/SaleOrderModal'
import { amountString, onCopyContent, ResProductDetail } from '../../../common'

type Props = RouteComponentProps<{
  productId: string
}>

export const Detail: React.FC<Props> = observer(({ match, history }) => {
  const [presentAlert] = useIonAlert()
  const [presentToast] = useIonToast()
  const { marketStore, commonStore, authStore } = useContext(StoresContext)
  const detailStore = marketStore.productDetailStore
  let loadParam = { productId: Number(match.params.productId) }

  useIonViewWillEnter(() => {
    commonStore.setShowTab(false)
    loadParam = { productId: Number(match.params.productId) }
    detailStore.load({ param: loadParam }, true)
  }, [match.params.productId])

  // 트레이드 탭 표시 - 마켓 페이지 진입시 탭 변경을 위한 스토어 값 저장
  useEffect(() => {
    if (detailStore.content?.status === 'SALE' && commonStore.listTab === 'TRADE') {
      commonStore.setListTab('SALE')
    } else if (detailStore.content?.status === 'TRADE') {
      commonStore.setListTab('TRADE')
    } else if (detailStore.content?.status === 'TRADE_STATUS') {
      commonStore.setListTab('TRADE_STATUS')
    } else {
      commonStore.setListTab('SALE')
    }
  }, [detailStore.content?.status, commonStore.listTab])

  // 조각 구매를 위한 모달 실행
  const [present, dismiss] = useIonModal(
    <IonNav
      root={() => <SaleOrderModal detailContent={detailStore.content as ResProductDetail} onDismiss={dismiss} />}
      swipeGesture={false}
    />,
  )

  const displayContentUrl = (url: string) => {
    if (url.startsWith('data:image')) {
      return url
    }
    return `https://static.sto.metagalaxia.com/${url}`
  }

  const onNotificationProduct = (favorite: boolean, productId: number) => {
    presentAlert({
      mode: 'md',
      header: `알림 ${favorite ? '해제' : '설정'}하시겠습니까?`,
      cssClass: 'custom-alert-alert ',
      buttons: [
        {
          text: '아니요',
          cssClass: 'alert-button-cancel',
        },
        {
          text: '네',
          cssClass: 'alert-button-confirm',
          handler: async () => {
            const favoriteResult = await marketStore.favoriteProduct(productId)
            if (favoriteResult) {
              await detailStore.load({ param: loadParam }, true)
              presentToast({
                message: '작품에 대한 알림을 받습니다.',
                duration: 3000,
                position: 'top',
                color: 'primary',
                icon: checkbox,
              })
              return
            }
            if (!favoriteResult) {
              await detailStore.load({ param: loadParam }, true)
              presentToast({
                message: '작품에 대한 알림 설정을 해제했습니다.',
                duration: 3000,
                position: 'top',
                color: 'danger',
                icon: warning,
              })
              return
            }
            return
          },
        },
      ],
    })
  }

  const onFavoriteProduct = async (productId: number) => {
    const favoriteResult = await marketStore.favoriteProduct(productId)
    if (favoriteResult) {
      await detailStore.load({ param: loadParam }, true)
      return
    }
    if (!favoriteResult) {
      await detailStore.load({ param: loadParam }, true)
      return
    }
    return
  }

  return (
    <IonPage>
      {detailStore.isLoaded && (
        <StyleIonHeader
          type={'SUB'}
          title={detailStore.content ? detailStore.content?.name : '작품 소개'}
          href={
            detailStore.content && detailStore.content?.status === 'TRADE'
              ? `/market/${match.params.productId}/trade`
              : '/market'
          }
        />
      )}
      <IonContent>
        <DetailContent detailStore={detailStore}>
          <ImgWrap>
            <img
              style={
                detailStore.content?.status === 'SALE' &&
                amountString(detailStore.content?.activeProductSale.remainCount.toString()) === '0'
                  ? {
                      opacity: '0.5',
                    }
                  : { opacity: '1' }
              }
              src={detailStore.content?.imageUri}
              alt={detailStore.content?.name}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null
                currentTarget.src = '/img/thumbnail.svg'
              }}
            />
          </ImgWrap>
          <ContentContainer>
            <ProductHeaderContainer>
              <ProductInfoWrap>
                <TextStyle type={'r_13'} color={'primary_light'}>
                  {detailStore.content?.artist}
                </TextStyle>
                <TextStyle type={'r_15'}>{detailStore.content?.name}</TextStyle>
              </ProductInfoWrap>
              <ProductIconWrap>
                <IconWrap
                  onClick={() =>
                    onCopyContent(presentToast, {
                      content: `${process.env.REACT_APP_STO_URL}${match.url}`,
                      successMsg: '해당 작품 링크가 복사되었습니다!',
                      failureMsg: '작품 링크 복사에 실패하였습니다.',
                    })
                  }
                >
                  <IonIcon style={{ marginLeft: '10px' }} icon={shareSocialOutline} />
                </IconWrap>
                <IconWrap
                  onClick={() =>
                    detailStore.content?.status === 'SALE' &&
                    detailStore.content?.activeProductSale.status === 'TO_BE_RELEASED'
                      ? onNotificationProduct(detailStore.content?.favorite, detailStore.content?.id)
                      : onFavoriteProduct(detailStore.content?.id as number)
                  }
                >
                  <IonIcon
                    style={{ marginLeft: '10px' }}
                    icon={detailStore.content?.favorite ? bookmark : bookmarkOutline}
                  />
                </IconWrap>
              </ProductIconWrap>
            </ProductHeaderContainer>
            <IonRouterLink color={'dark'} routerLink={`/market/${match.params.productId}/community`}>
              <CommunityBox>
                <IonIcon className="grid" icon={gridOutline}></IonIcon>
                <TextWrap>
                  <TextStyle type={'r_15'}>해당 작품 커뮤니티</TextStyle>
                </TextWrap>
                <IonIcon icon={chevronForward}></IonIcon>
              </CommunityBox>
            </IonRouterLink>
            {/* 판매 예정 */}
            {detailStore.content?.status === 'SALE' &&
              detailStore.content?.activeProductSale.status === 'TO_BE_RELEASED' && (
                <>
                  <BetweenWrap>
                    <TextStyle type="r_15">공개 예정일</TextStyle>
                    <TextStyle type="b_15">
                      {`${moment(new Date(detailStore.content?.activeProductSale.releaseDate)).format('YYYY.MM.DD')}`}
                    </TextStyle>
                  </BetweenWrap>
                </>
              )}
            {/* 조각 판매중 */}
            {detailStore.content?.status === 'SALE' && detailStore.content?.activeProductSale.status === 'RELEASE' && (
              <>
                <BetweenWrap>
                  <TextStyle type="r_15">판매 기간</TextStyle>
                  <TextStyle type="b_15">
                    {`${moment(new Date(detailStore.content?.activeProductSale.releaseDate)).format(
                      'YYYY.MM.DD',
                    )} ~ ${moment(new Date(detailStore.content?.activeProductSale.untilDate)).format('YYYY.MM.DD')}`}
                  </TextStyle>
                </BetweenWrap>
                <BetweenWrap>
                  <TextStyle type="r_15">판매율</TextStyle>
                  <TextStyle type="b_15">
                    {`${amountString(
                      (
                        ((detailStore.content?.tokenCount - detailStore.content?.activeProductSale.remainCount) /
                          detailStore.content?.tokenCount) *
                        100
                      ).toString(),
                      false,
                      2,
                    )} %` || '-'}
                  </TextStyle>
                </BetweenWrap>
                <BetweenWrap>
                  <TextStyle type="r_15">판매 금액</TextStyle>
                  <TextStyle type="b_15">
                    <IonText>{`${amountString(detailStore.content?.saleCost.toString())} 원` || '-'}</IonText>
                  </TextStyle>
                </BetweenWrap>
                <BetweenWrap>
                  <TextStyle type="r_15">전체 조각 수</TextStyle>
                  <TextStyle type="b_15">
                    <IonText>{`${amountString(detailStore.content?.tokenCount.toString())} 개` || '-'}</IonText>
                  </TextStyle>
                </BetweenWrap>
                <BetweenWrap>
                  <TextStyle type="r_15">남은 조각 수</TextStyle>
                  <TextStyle type="b_15">
                    <IonText
                      style={
                        amountString(detailStore.content?.activeProductSale.remainCount.toString()) === '0'
                          ? {
                              color: 'red',
                            }
                          : {}
                      }
                    >
                      {amountString(detailStore.content?.activeProductSale.remainCount.toString()) === '0'
                        ? 'Sold out'
                        : `${amountString(detailStore.content?.activeProductSale.remainCount.toString())} 개` || '-'}
                    </IonText>
                  </TextStyle>
                </BetweenWrap>
              </>
            )}
            {/* 트레이드중 */}
            {detailStore.content?.status === 'TRADE' && (
              <>
                <BetweenWrap>
                  <TextStyle type="r_15">총 판매 금액</TextStyle>
                  <TextStyle type="b_15">
                    <IonText>{`${amountString(detailStore.content?.totalTradeBalance?.toString()) ?? 0} 원`}</IonText>
                  </TextStyle>
                </BetweenWrap>
                <BetweenWrap>
                  <TextStyle type="r_15">누적 판매 조각 수</TextStyle>
                  <TextStyle type="b_15">
                    <IonText>{`${amountString(detailStore.content?.tokenCount?.toString()) ?? 0} 개`}</IonText>
                  </TextStyle>
                </BetweenWrap>
              </>
            )}
            {detailStore.content?.contents?.map((d, i) => (
              <CategoryWrap key={i}>
                <IonText className={d.type === 'header' ? 'category-title' : 'category-content'}>
                  {d.type === 'header' && <IonLabel>{d.content === '' ? '-' : d.content}</IonLabel>}
                  {d.type === 'body' && <div>{d.content === '' ? '-' : d.content}</div>}
                  {/* {d.content || '-'} */}
                  {d.type === 'image' && (
                    <>
                      <IonImgForContent
                        alt="컨텐츠 이미지"
                        src={displayContentUrl(d.content)}
                        onIonError={(e) => {
                          e.target.src = '/img/thumbnail.svg'
                        }}
                      />
                    </>
                  )}
                </IonText>
              </CategoryWrap>
            ))}
          </ContentContainer>
        </DetailContent>
      </IonContent>
      {!detailStore.isLoaded && null}
      <DetailContent detailStore={detailStore}>
        {detailStore.content?.status === 'SALE' &&
          detailStore.content?.activeProductSale.status === 'TO_BE_RELEASED' &&
          (detailStore.content?.favorite ? (
            <FooterButtons
              button={{
                color: 'danger',
                text: '알림 해제',
                onClick: () =>
                  onNotificationProduct(Boolean(detailStore.content?.favorite), Number(detailStore.content?.id)),
              }}
            />
          ) : (
            <FooterButtons
              button={{
                color: 'primary',
                text: '알림 설정',
                onClick: () =>
                  onNotificationProduct(Boolean(detailStore.content?.favorite), Number(detailStore.content?.id)),
              }}
            />
          ))}
        {detailStore.content?.status === 'SALE' &&
          detailStore.content?.activeProductSale.status === 'RELEASE' &&
          detailStore.content?.activeProductSale.remainCount !== 0 && (
            <FooterButtons
              button={{
                color: 'dark',
                text: authStore.userInfo == null ? '구매하시려면 로그인해주세요' : '구매하기',
                onClick: authStore.userInfo == null ? () => history.push('/signIn') : () => present(),
              }}
            />
          )}
        {detailStore.content?.status === 'TRADE' && null}
      </DetailContent>
    </IonPage>
  )
})

const ImgWrap = styled.div`
  width: 100%;
  aspect-ratio: 1/1;
  display: flex;
  text-align: center;
  justify-content: center;
  img {
    object-fit: contain;
  }
`

const ContentContainer = styled.div`
  padding: 25px 20px;
`

const CommunityBox = styled.div`
  margin: 30px 0;
  width: 100%;
  background-color: var(--ion-color-gray100);
  padding: 17px 8px 17px 23px;
  border-radius: 7px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  ion-icon {
    font-size: 24px;
  }
`

const TextWrap = styled.div`
  margin-left: 15px;
  width: 100%;
`

const BetweenWrap = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 15px;
`

const CategoryWrap = styled.div`
  display: flex;
  flex-direction: column;
  white-space: pre-line;
  .category-title {
    font-weight: 900;
    font-size: 20px;
    margin: 20px 0;
  }
  .category-content {
    font-size: 15px;
    margin-bottom: 20px;
  }
`

const ProductHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const ProductInfoWrap = styled.div`
  display: flex;
  flex-direction: column;
`

const ProductIconWrap = styled.span`
  min-width: 80px;
  max-height: 30px;
  font-size: 20px;
  margin-top: 7px;
`

const IconWrap = styled.span`
  font-size: 25px;
  margin-left: 10px;
  cursor: pointer;
`
const IonImgForContent = styled(IonImg)`
  width: 100%;
  height: 100%;
  object-fit: contains;
`
